// src/services/routeRegistry.js
import logger from '../utils/logger';

class RouteRegistry {
  constructor() {
    this.routes = [];
    this.initialized = false;
  }

  /**
   * Register routes from API data
   */
  registerRoutes(routesData) {
    if (!Array.isArray(routesData)) {
      console.error("Invalid routes data, expected array");
      return false;
    }

    this.routes = routesData.map(route => ({
      id: route.id,
      path: route.path,
      title: route.title || 'Untitled Route',
      description: route.description || '',
      public: route.public || false,
      is_active: route.is_active !== undefined ? route.is_active : true,
      roles: route.roles || []
    }));
    
    this.initialized = true;
    logger.log(`RouteRegistry: Registered ${this.routes.length} routes`);
    return true;
  }

  /**
   * Check if a user has access to a route
   */
  hasAccess(routePath, userRoles) {
    // If routes aren't loaded yet, deny access (components will re-render after routes load)
    if (!this.initialized) {
      return false;
    }

    // Use getRoute method which handles dynamic routes
    const route = this.getRoute(routePath);
    if (!route) {
      // Route not found - silently deny access (route may be blocked by backend)
      return false;
    }

    // Admin always has access to active routes
    if (userRoles.includes('admin')) {
      return true;
    }

    // Public routes are accessible to all authenticated users
    // BUT check if user's roles are in the blacklist
    if (route.public) {
      // If route has roles assigned, they act as BLACKLIST for public routes
      if (Array.isArray(route.roles) && route.roles.length > 0) {
        // Check if any of user's roles are in the blacklist
        const isBlocked = userRoles.some(role => route.roles.includes(role));
        return !isBlocked; // Allow access only if NOT blocked
      }
      return true; // Public route with no roles = accessible to all
    }

    // Private routes: Check if user has any of the required roles (WHITELIST)
    // If no roles assigned, block access (admin only)
    if (!Array.isArray(route.roles) || route.roles.length === 0) {
      return false;
    }
    return userRoles.some(role => route.roles.includes(role));
  }

  /**
   * Get all routes
   */
  getAllRoutes() {
    return this.routes.map(route => ({
      ...route,
      active: route.is_active
    }));
  }

  /**
   * Get routes accessible by the user
   */
  getAccessibleRoutes(userRoles) {
    // Filter out inactive routes for everyone
    const activeRoutes = this.routes.filter(route => route.is_active);

    if (userRoles.includes('admin')) {
      // Admin can access all active routes - map to include active property
      return activeRoutes.map(route => ({ ...route, active: route.is_active }));
    }

    const filtered = activeRoutes.filter(route => {
      // Public routes with blacklist logic
      if (route.public) {
        // If route has roles, they act as BLACKLIST
        if (Array.isArray(route.roles) && route.roles.length > 0) {
          // Block if user has any blacklisted role
          const isBlocked = userRoles.some(role => route.roles.includes(role));
          return !isBlocked;
        }
        return true; // Public with no roles = accessible
      }

      // Private routes with whitelist logic
      if (!Array.isArray(route.roles) || route.roles.length === 0) {
        // Private route with no roles assigned = not accessible (admin only)
        return false;
      }
      return route.roles.some(role => userRoles.includes(role));
    });

    // Map to include active property for compatibility
    return filtered.map(route => ({ ...route, active: route.is_active }));
  }

   /**
   * Get a specific route by its path
   * @param {string} routePath - The path of the route to find (e.g., '/dashboard')
   * @returns {object | undefined} The route object if found and active, otherwise undefined.
   */
  getRoute(routePath) {
    if (!routePath) {
      return undefined;
    }
    
    // First try exact match
    let route = this.routes.find(r => r.path === routePath);
    
    // If no exact match, try pattern matching for dynamic routes
    if (!route) {
      route = this.routes.find(r => {
        // Convert route pattern to regex
        // Replace :param with regex pattern to match any value
        const pattern = r.path.replace(/:[^/]+/g, '[^/]+');
        const regex = new RegExp(`^${pattern}$`);
        return regex.test(routePath);
      });
    }
    
    // Return undefined if route is inactive
    if (route && !route.is_active) {
      return undefined;
    }
    // Map is_active to active for compatibility
    if (route) {
      return {
        ...route,
        active: route.is_active
      };
    }
    return route;
  }

  /**
   * Clear all routes (used during logout)
   */
  clear() {
    this.routes = [];
    this.initialized = false;
    logger.log('RouteRegistry: Cleared all routes');
  }

}

const routeRegistry = new RouteRegistry();
export default routeRegistry;